import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // The pet owner's survey. Public and standalone: no JWT, no app chrome. The
    // key comes from a text message and is held in the route for the session
    // only. Registered first so nothing else can shadow it.
    {
      path: '/r/:key',
      name: 'survey',
      component: () => import('@/views/SurveyView.vue'),
      meta: { public: true, standalone: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
    // The password-link screens. Public and standalone, because whoever follows
    // one of these has no usable session by definition -- that is the point of
    // the link. The /auth/* prefix is what vetreviews-gateway's auth/emails.py
    // builds, and matches insights.
    //
    // Until these existed, reviews.vetware.io had no route for any of them and
    // the catch-all below redirected to '/', which silently dropped the token
    // and bounced the person to a login they could not get past.
    {
      path: '/auth/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { public: true, standalone: true },
    },
    {
      path: '/auth/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { public: true, standalone: true },
    },
    {
      path: '/auth/verify-email',
      name: 'verify-email',
      component: () => import('@/views/VerifyEmailView.vue'),
      meta: { public: true, standalone: true },
    },
    // Unprefixed aliases. send_user_invite built /reset-password before the
    // prefix was settled, and a redirect that keeps the query is the difference
    // between an old link working and the token vanishing into the catch-all.
    // `redirect` preserves query and hash by default.
    { path: '/reset-password', redirect: { name: 'reset-password' } },
    { path: '/verify-email', redirect: { name: 'verify-email' } },
    { path: '/forgot-password', redirect: { name: 'forgot-password' } },
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/responses',
      name: 'responses',
      component: () => import('@/views/ResponsesView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/responses/:id',
      name: 'response',
      component: () => import('@/views/ResponseDetailView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/survey',
      name: 'survey-builder',
      component: () => import('@/views/SurveyBuilderView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: { requiresAuth: true },
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  // A public route must never wait on, or trigger, an auth round trip. The
  // survey is opened by someone with no account and it has to render instantly.
  if (to.meta.public) return true

  if (auth.token && !auth.user) await auth.fetchMe()
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  return true
})

export default router
